import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { BaseComponent } from '../base/base.component';
import { OrgImportData, StaffImportData, StudentImportData } from '../data/org-import';
import { SyncLog } from '../data/sync-log';
import { FormatUtilities } from '../utilities/format-utilities';
import { Course } from '../data/course';

@Component({
  selector: 'app-org-import',
  templateUrl: './org-import.component.html',
  styleUrls: ['./org-import.component.scss']
})

export class OrgImportComponent extends BaseComponent implements OnInit {
  longDateFormat = FormatUtilities.longDateFormat;

  orgId: number;

  lastSync: SyncLog;

  workbook: XLSX.WorkBook;
  importedOrg: OrgImportData;
  classData: any[];
  courses: Course[];

  errors: string[] = [];
  warnings: string[] = [];

  importing: boolean = false;

  columns = {
    staffName: "Staff Full Name",
    staffEmail: "Staff Email Address",
    staffRole: "Staff Role: Teacher or Admin",
    className: "Class Name",
    classTeacher: "Staff Email Address",
    classLevel: "Joy School English Level",
    studentClass: "Class Name",
    studentName: "Student Full Name",
    studentId: "Student ID",
    studentParentName: "Parent Name",
    studentParentEmail: "Parent Email",
    studentGrade: "Student Grade Level"
  };

  ngOnInit() {
    this.route.params.subscribe(params =>
      {
        this.orgId = params.organizationId;
        this.dataService.getLastSync(this.orgId).subscribe(s => {
          this.lastSync = s;
        });
      });
    this.dataService.getCourses().subscribe(c => this.courses = c);
  }

  async handleFileDrop(event)
  {
    this.importedOrg = undefined;
    this.workbook = undefined;
    const file = event.dataTransfer.files[0];
    const fileValid = this.validateFile(file);
    if (!fileValid) return;
    const data = await file.arrayBuffer();
    this.workbook = XLSX.read(data);
    this.parseWorkbook();
  }

  async handleFileInput(event)
  {
    event.preventDefault();
    this.importedOrg = undefined;
    this.workbook = undefined;
    const file = event.target.files[0];
    const fileValid = this.validateFile(file);
    if (!fileValid) return;
    const data = await file.arrayBuffer();
    this.workbook = XLSX.read(data);
    this.parseWorkbook();
  }

  validateFile(file: any): boolean {
    let fileValid = file.name.endsWith(".xlsx");
    if (!fileValid) {
      this.errors = ["Uploaded file must be an Excel file (.xlsx)"];
    }
    return fileValid;
  }

  parseWorkbook()
  {
    this.errors = [];
    this.warnings = [];

    var staffSheet = this.workbook.Sheets["STAFF"];
    var classesSheet = this.workbook.Sheets["CLASSES"];
    var studentsSheet = this.workbook.Sheets["STUDENTS"];

    var staffList = XLSX.utils.sheet_to_json(staffSheet, { range: 1 });
    var classesList = XLSX.utils.sheet_to_json(classesSheet, { range: 1 });
    var studentsList = XLSX.utils.sheet_to_json(studentsSheet, { range: 1 });

    if (staffList.length == 0 && classesList.length == 0 && studentsList.length == 0) {
      this.errors = ["The uploaded file was blank or could not be understood. Double check that you uploaded a file matching the provided template."];
      return;
    }

    const staffValid = this.validateStaff(staffList);
    const classesValid = this.validateClasses(classesList, staffList);
    const studentsValid = this.validateStudents(studentsList, classesList);

    if (staffValid && classesValid && studentsValid) this.buildJson(staffList, classesList, studentsList);
  }

  buildJson(staffList, classesList, studentsList)
  {
    this.importedOrg = new OrgImportData();

    this.importedOrg.Staff = staffList.map(staff => ({
      name: staff[this.columns.staffName], 
      email: staff[this.columns.staffEmail], 
      role: staff[this.columns.staffRole],
      class: null
    }));

    this.importedOrg.Students = studentsList.map(student => ({
      name: student[this.columns.studentName],
      externalId: student[this.columns.studentId],
      class: student[this.columns.studentClass],
      parentName: student[this.columns.studentParentName],
      parentEmail: student[this.columns.studentParentEmail],
      grade: "" + student[this.columns.studentGrade],
      courseCode: ""
    }));

    this.classData = [];

    classesList.forEach(_class => {
      var teacherEmail = _class[this.columns.classTeacher];
      var courseCode = this.levelToCoursecode(_class[this.columns.classLevel]);
      var className = _class[this.columns.className];

      var teacher = this.importedOrg.Staff.find(staff => staff.email == teacherEmail);
      if (teacher) teacher.class = className;

      var existingClass = this.classData.find(_class => _class.name === className);

      if (existingClass) {
        if (teacher) {
          if (existingClass.teacherName) 
            existingClass.teacherName += `, ${teacher.name}`;
          else
            existingClass.teacherName = teacher.name;
        }

        return;
      };

      this.importedOrg.Students.filter(student => student.class == className).forEach(
        student => student.courseCode = courseCode
      );

      this.classData.push({
        name: className,
        teacherName: teacher && teacher.name,
        level: _class[this.columns.classLevel]
      });
    });
    
    this.importedOrg.Classes = this.classData.map(_class => _class.name);
  }

  
  levelToCoursecode(level: string): string {
    if (level == "") return null;
    if (level === undefined) return null;
    if (level === null) return null;

    let course = this.courses.find(c => c.ShortDescription == level);
    if (course) return course.Code;
    return null;
  }

  // Returns true if there are no errors
  validateStaff(teachersList): boolean
  {
    var noErrors = true;
    var emails = {};

    for (let index = 0; index < teachersList.length; index++) {
      const teacher = teachersList[index];
      const name = teacher[this.columns.staffName];
      const email = teacher[this.columns.staffEmail];
      const role = teacher[this.columns.staffRole];
      
      const errorId = name || `at Row ${index + 2}`;

      // Blank rows can be ignored, but show a warning
      if (!email && !name && !role)
      {
        this.warnings.push(`Staff ${errorId} was skipped because it was blank`);
        continue;
      } 

      if (!name)
      {
        this.errors.push(`Staff ${errorId} requires a name`);
        noErrors = false;
      }

      if (!email)
      {
        this.errors.push(`Staff ${errorId} requires an email address`);
        noErrors = false;
      }

      if (!role)
      {
        this.errors.push(`Staff ${errorId} requires a role`);
        noErrors = false;
      }
      else if (role.toLowerCase() != "teacher" && role.toLowerCase() != "admin")
      {
        this.errors.push(`Staff ${errorId} role must be either "Teacher" or "Admin"`);
        noErrors = false;
      }

      if (!emails[email])
      {
        emails[email] = [];
      }
      emails[email].push(errorId);
    }

    for (const email in emails) {
      if (Object.prototype.hasOwnProperty.call(emails, email)) {
        const duplicates = emails[email];
        if (duplicates.length > 1)
        {
          this.errors.push(`Multiple staff with email address ${email}: ${duplicates.join(", ")}`);
          noErrors = false;
        }
      }
    }

    return noErrors;
  }

  // Returns true if there are no errors
  validateClasses(classesList, staffList): boolean
  {
    var noErrors = true;

    var staffEmails = staffList.map(staff => staff[this.columns.staffEmail]);

    for (let index = 0; index < classesList.length; index++) {
      const _class = classesList[index];
      const className = _class[this.columns.className];
      const teacherEmail = _class[this.columns.classTeacher];
      const classLevel = _class[this.columns.classLevel];
      
      const errorId = className || `at Row ${index + 2}`;

      if (!className)
      {
        this.errors.push(`Class ${errorId} requires a name`);
        noErrors = false;
      }
      
      if (!teacherEmail)
      {
        this.warnings.push(`Class ${errorId} has no teacher`);
      }
      else
      {
        var teacher = staffEmails.find(staffEmail => staffEmail === teacherEmail);
        if (!teacher)
        {
          this.errors.push(`The Teacher with email ${teacherEmail} for Class ${errorId} was not found in the Staff tab`);
          noErrors = false;
        }
      }
      
      if (classLevel) {
        var level = ["1","2","3","level 1", "level 2", "level 3", "older beginner", "placement test", ""].find(level => level === classLevel.toString().toLowerCase());
        if (!level)
        {
          this.errors.push(`Class ${errorId} level must be "Level 1", "Level 2", or "Level 3"`);
        }
      }
    }

    return noErrors;
  }

  // Returns true if there are no errors
  validateStudents(studentsList, classesList): boolean
  {
    var noErrors = true;
    var allGradesPresent: boolean = true;
    var externalIds = {};
    var missingExternalIds = false;

    var classNames = classesList.map(_class => _class[this.columns.className]);

    for (let index = 0; index < studentsList.length; index++) {
      const student = studentsList[index];
      const className = student[this.columns.studentClass];
      const name = student[this.columns.studentName];
      const externalId = student[this.columns.studentId];
      const parentName = student[this.columns.studentParentName];
      const parentEmail = student[this.columns.studentParentEmail];
      const studentGrade = student[this.columns.studentGrade];
      
      const errorId = name || `at Row ${index + 2}`;

      if (!name)
      {
        this.errors.push(`Student ${errorId} requires a name`);
        noErrors = false;
      }

      if (!className)
      {
        this.errors.push(`Student ${errorId} requires a class`);
        noErrors = false;
      }
      else
      {
        var _class = classNames.find(_class => _class === className);
        if (!_class)
        {
          this.errors.push(`The Class ${className} for Student ${errorId} was not found in the Classes tab`);
          noErrors = false;
        }
      }

      if (parentName && !parentEmail) {
        this.errors.push(`The Parent for Student ${errorId} requires an email address`);
        noErrors = false;
      }

      if (!externalId)
      {
        missingExternalIds = true;
      }
      else {
        if (!externalIds[externalId])
        {
          externalIds[externalId] = [];
        }
        externalIds[externalId].push(errorId);
      }

      if (!studentGrade) {
        allGradesPresent = false;
      }
    }

    for (const id in externalIds) {
      if (Object.prototype.hasOwnProperty.call(externalIds, id)) {
        const duplicates = externalIds[id];
        if (duplicates.length > 1)
        {
          this.errors.push(`Multiple students with ID ${id}: ${duplicates.join(", ")}`);
          noErrors = false;
        }
      }
    }

    if (missingExternalIds) {
      this.warnings.push("Some students were missing IDs. New IDs will be generated for them.");
    }

    if (!allGradesPresent) {
      this.errors.push("Grades are required for all students.");
    }

    return noErrors;
  }

  uploadOrg()
  {
    this.importing = true;
    this.dataService.importOrganization(this.orgId, this.importedOrg).subscribe(response => {
        this.importing = false;
        if (response === null || response.Success) {
          Swal.fire("Success", "Success", "success");
        }
      });
  }

  startImport(event: Event) {
    event.stopPropagation();
    if (this.lastSync.LastSyncDate) {
      Swal.fire({
        title: 'Are you sure?',
        html: `This will delete your current rosters and import your new rosters`,
        type: 'warning',
        showConfirmButton: true,
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Import"
      }).then(res => {
        if (res.value) {
          this.uploadOrg();
        }
      });
    }
    else {
      this.uploadOrg();
    }
  }

  downloadRoster() {
    this.dataService.getSchoolRoster(this.orgId).subscribe(o => {
      var workbook = XLSX.utils.book_new();

      var staffFormatted = [];
      o.Staff.forEach(staff => {
        var staffObject = {};
        staffObject[this.columns.staffName] = staff.Name;
        staffObject[this.columns.staffEmail] = staff.Email;
        staffObject[this.columns.staffRole] = staff.RoleCode == "GADMIN" ? "Admin" : "Teacher";
        staffFormatted.push(staffObject);
      });
      var staffSheet = XLSX.utils.json_to_sheet([{}]);
      XLSX.utils.sheet_add_json(staffSheet, staffFormatted, { origin:"A2" });
      XLSX.utils.book_append_sheet(workbook, staffSheet, "STAFF");

      var classesFormatted = [];
      o.Classes.forEach(Class => {
        var classObject = {};
        classObject[this.columns.className] = Class.Description;
        classObject[this.columns.classTeacher] = Class.TeacherEmail;
        classesFormatted.push(classObject);
      });
      var classesSheet = XLSX.utils.json_to_sheet([{}]);
      XLSX.utils.sheet_add_json(classesSheet, classesFormatted, { origin:"A2" });
      XLSX.utils.book_append_sheet(workbook, classesSheet, "CLASSES");

      var studentsFormatted = [];
      o.Students.forEach(student => {
        var studentObject = {};
        studentObject[this.columns.studentClass] = student.ClassName;
        studentObject[this.columns.studentName] = student.Name;
        studentObject[this.columns.studentId] = student.ExternalID;
        studentObject[this.columns.studentParentEmail] = student.ParentEmail;
        studentObject[this.columns.studentParentName] = student.ParentName;
        studentObject[this.columns.studentGrade] = student.Grade;
        studentsFormatted.push(studentObject);
      })
      var studentsSheet = XLSX.utils.json_to_sheet([{}]);
      XLSX.utils.sheet_add_json(studentsSheet, studentsFormatted, { origin:"A2" });
      XLSX.utils.book_append_sheet(workbook, studentsSheet, "STUDENTS");

      var timestamp = new Date().toISOString();

      XLSX.writeFileXLSX(workbook, "roster-"+timestamp+".xlsx");
    });
  }

  GetCourseDescriptionFromLevel(level: number): string {
    return this.courses.find(c => c.NumberOnly === level).ShortDescription;
  }

  getRoleCode(role: string): string {
    if (role.toLowerCase() === "teacher") {
      return "TEACH";
    }
    else if (role.toLowerCase() === "admin") {
      return "GADMIN";
    }
    else return role;
  }

  getStudentsForClass(className: string): StudentImportData[]
  {
    return this.importedOrg.Students.filter(student => student.class === className);
  }

  studentParentText(student: StudentImportData): string {
    if (!student.parentEmail) return "";
    if (!student.parentName) return ` — Parent: ${student.parentEmail}`;
    return ` — Parent: ${student.parentName}, ${student.parentEmail}`
  }
}
